import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Box, Typography } from '@mui/material';
import * as React from 'react';
import {
  Question,
  QuestionFormResponse,
} from '../../questionnaire/types/questionnaire.types';
import { formatResponse } from '../utils/responseFormatter';

interface ResponseDisplayProps {
  question: Question;
  response: QuestionFormResponse | undefined;
}

export const ResponseDisplay: React.FC<ResponseDisplayProps> = ({
  question,
  response,
}) => {
  const hasResponse = !!response;

  // Renderização especial para multiple_choice_checkbox
  if (
    question.type === 'multiple_choice_checkbox' &&
    response?.questionType === 'multiple_choice_checkbox'
  ) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: '12px',
          background: 'rgba(16, 185, 129, 0.05)',
          border: '1px solid #7854AB',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto-Medium !important',
            fontSize: '10pt !important',
            color: '#6b7280 !important',
            mb: 2,
          }}
        >
          Resposta:
        </Typography>
        {response.response.answers.map((answer) => (
          <Box
            key={answer.answerId}
            sx={{
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <CheckBoxIcon
              sx={{
                color: '#7854AB',
                fontSize: '20px',
              }}
            />
            <Typography
              sx={{
                fontFamily: 'Roboto-Regular !important',
                fontSize: '10pt !important',
                color: '#374151 !important',
              }}
            >
              {answer.answerText}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  // Renderização especial para ranking
  if (question.type === 'ranking' && response?.questionType === 'ranking') {
    const sortedAnswers = [...response.response.rankedAnswers].sort(
      (a, b) => a.rank - b.rank
    );

    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: '12px',
          background: 'rgba(16, 185, 129, 0.05)',
          border: '1px solid #7854AB',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto-Medium !important',
            fontSize: '10pt !important',
            color: '#6b7280 !important',
            mb: 2,
          }}
        >
          Resposta (ordenado por prioridade):
        </Typography>
        {sortedAnswers.map((answer) => (
          <Box
            key={answer.answerId}
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: '8px',
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Roboto-Bold !important',
                fontSize: '14pt !important',
                color: '#7854AB !important',
                minWidth: '30px',
                textAlign: 'center',
              }}
            >
              {answer.rank}º
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Roboto-Regular !important',
                fontSize: '10pt !important',
                color: '#374151 !important',
              }}
            >
              {answer.answerText}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  // Renderização especial para matrix_scale
  if (
    question.type === 'matrix_scale' &&
    response?.questionType === 'matrix_scale'
  ) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: '12px',
          background: 'rgba(16, 185, 129, 0.05)',
          border: '1px solid #7854AB',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto-Medium !important',
            fontSize: '10pt !important',
            color: '#6b7280 !important',
            mb: 2,
          }}
        >
          Resposta:
        </Typography>
        {response.response.matrixAnswers.map((answer) => (
          <Box
            key={answer.answerId}
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: '8px',
              background: '#ffffff',
              border: '1px solid #e5e7eb',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Roboto-Medium !important',
                fontSize: '10pt !important',
                color: '#374151 !important',
                mb: 0.5,
              }}
            >
              {answer.rowText}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Roboto-Regular !important',
                fontSize: '10pt !important',
                color: '#7854AB !important',
              }}
            >
              → {answer.answerText}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  // Renderização padrão para outros tipos
  const responseText = response
    ? formatResponse(question, response)
    : 'Não respondida';

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: '12px',
        background: hasResponse
          ? 'rgba(16, 185, 129, 0.05)'
          : 'rgba(156, 163, 175, 0.05)',
        border: '1px solid',
        borderColor: hasResponse ? '#7854AB' : '#9ca3af',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Roboto-Medium !important',
          fontSize: '10pt !important',
          color: '#6b7280 !important',
          mb: 1,
        }}
      >
        Resposta:
      </Typography>
      <Typography
        component="div"
        sx={{
          fontFamily: 'Roboto-Regular !important',
          fontSize: '11pt !important',
          color: '#374151 !important',
          whiteSpace: 'pre-line',
          wordBreak: 'break-word',
        }}
        dangerouslySetInnerHTML={{ __html: responseText }}
      />
    </Box>
  );
};
